import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { addAddonToCartPDPAction } from "../../../store/slices/pdpAddonSlice";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { ga4addonAdded } from "../../../analytics/gaEvents";
import useAnalytics from "../../../analytics/useAnalytics";

/**
 * Builds the shared add-on "continue" handler: submits whatever add-ons are currently
 * selected (or none, when called with `skipAddons`) together with the base product's
 * delivery details - the same dispatch the footer's own Continue button uses to actually
 * commit the cart write. Shared so other surfaces (e.g. the mobile pre-checkout drawer's
 * close button) can complete the flow "without adding add-ons" instead of abandoning it.
 *
 * @param {Object} params - Hook params.
 * @param {Object} params.commonCleverTapData - Common CleverTap payload.
 * @returns {(skipAddons?: boolean) => void} continueWithAddons handler.
 */
export const useAddonContinueHandler = ({ commonCleverTapData }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { pdpSelectedAddons, pdpAddonInfo, currentItem, checkLoginStatus, pincode, selectedCurrency, selectedDate } =
    useSelector(
      (state) => ({
        pdpSelectedAddons: state.pdpAddon.selectedAddons,
        pdpAddonInfo: state.pdpAddon?.addOnInfo,
        currentItem: state?.pdp?.clientCurrentClickedItem,
        checkLoginStatus: state.fus.loginDetail?.loggedin,
        pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
        selectedCurrency: state?.currencyList?.userSelectedCurrency?.currency?.id,
        selectedDate: state.shippingDateAndSlotSelection?.selectedDeliveryDetails?.selectedDate,
      }),
      shallowEqual,
    );

  const addons = pdpSelectedAddons;
  const addonInfo = pdpAddonInfo;

  return (skipAddons = false) => {
    const effectiveAddons = skipAddons ? [] : addons;

    const formData = new FormData();
    const isDomestic = currentItem?.countryGeoId === "IND";
    const payload = {
      countryGeoId: isDomestic ? "" : currentItem?.countryGeoId,
      itemDesiredDeliveryDate: selectedDate,
      masterProductId: addonInfo?.masterProductId,
      orderItemSeqId: "ID" + addonInfo?.orderItemSeqId,
      pinCode: addonInfo?.parentItemDetails?.pinCode,
      ...(!isDomestic && {
        shippingMethodId: addonInfo?.parentItemDetails?.shippingMethodId,
        timeSlotId: addonInfo?.parentItemDetails?.timeSlotId,
      }),
    };
    for (const key in payload) {
      formData.append(key, payload[key]);
    }
    effectiveAddons.forEach((item) => {
      const itemP = `${item.productId}_${item.quantity}`;
      formData.append("addonProductId", item.isFromCart ? `${itemP}_${item.itemIndex}` : itemP);
    });
    const queryString = new URLSearchParams(formData).toString();

    const gaProperties = ga4addonAdded(effectiveAddons, pincode, checkLoginStatus, "PDP", selectedCurrency);

    dispatch(addAddonToCartPDPAction({ param: queryString }));

    const totalAddonQuantity = effectiveAddons.reduce((count, addon) => count + addon.quantity, 0);
    const totalAddonPrice = effectiveAddons.reduce((total, addon) => total + addon.price * addon.quantity, 0);

    trackEvent(
      cleverTapCommonEvents.addonContinueClicked,
      {
        ...commonCleverTapData,
        total_addon_quantity: totalAddonQuantity,
        total_addon_price: totalAddonPrice,
        is_addon_added: effectiveAddons.length > 0,
        is_date_today: null,
        is_date_tomorrow: null,
        is_variant_selected: null,
        flow: "new",
      },
      ["clevertap"],
    );
    if (effectiveAddons.length > 0) {
      trackEvent(GA4_EVENTS?.ADDONS_ADDED, { ...gaProperties }, ["ga4"]);
    } else {
      trackEvent(GA4_EVENTS?.CONTINUE_WITHOUT_ADDON, {}, ["ga4"]);
    }
  };
};
